import { ChevronDown } from 'lucide-react';

import { Menu } from '@/components/ui/Menu';
import { SecureImage } from '@/components/ui/SecureImage';
import { cn, getNameInitials } from '@/lib/utils';
import { parentApi } from '../api/parent.api';
import { useParentChild, useSelectedChild } from '../store/child.store';

/**
 * Which child the panel is about, at the top of the sidebar.
 *
 * It sits above the menu rather than inside a page because every screen behind
 * that menu is about one child: a parent of two reads "Attendance" as "whose
 * attendance", and the answer has to be in view while they choose where to go.
 *
 * A family with one child gets the card without a control — a dropdown with a
 * single option is a button that does nothing, and it invites the reader to
 * look for a second child they do not have.
 *
 * Drawn for the sidebar's dark sheet, so the surfaces here are the rail's own
 * translucent whites rather than page tokens, which would be invisible on it.
 */

/** The line under the name: the class, and the roll number if there is one. */
function childMeta(className: string, rollNumber: string | null): string {
  return rollNumber ? `${className} · Roll #${rollNumber}` : className;
}

const CARD =
  'flex w-full items-center gap-3 rounded-[16px] border border-white/15 bg-white/[.06] px-3 py-2.5 text-left shadow-[inset_0_1px_0_rgba(255,255,255,.07)]';

export function ChildSwitcher() {
  const children = useParentChild((state) => state.children);
  const select = useParentChild((state) => state.select);
  const child = useSelectedChild();

  if (!child) return null;

  const body = (
    <>
      <span
        aria-hidden
        className="relative grid h-10 w-10 shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 text-[13px] font-black text-white ring-2 ring-white/40"
      >
        {getNameInitials(child.fullName)}
        {/* The photograph the office uploaded, over the initials — which show
            through while it loads or if it cannot be fetched. */}
        {child.hasPhoto && (
          <SecureImage
            path={parentApi.childPhotoPath(child.uuid)}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-bold text-white">{child.fullName}</span>
        <span className="block truncate text-xs font-semibold text-indigo-200">
          {childMeta(child.className, child.rollNumber)}
        </span>
      </span>
    </>
  );

  if (children.length < 2) {
    return <div className={CARD}>{body}</div>;
  }

  return (
    <Menu
      label="Switch to another child"
      align="start"
      triggerClassName={cn(CARD, 'cursor-pointer transition-colors hover:bg-white/10')}
      trigger={
        <>
          {body}
          <ChevronDown className="h-4 w-4 shrink-0 text-indigo-200" aria-hidden />
        </>
      }
      items={children.map((option) => ({
        key: option.uuid,
        label: option.fullName,
        description: childMeta(option.className, option.rollNumber),
        onSelect: () => select(option.uuid),
      }))}
    />
  );
}
